import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Popover from './Popover';
import { useDashboardFilters } from './useDashboardFilters';
import { useProperties } from './useProperties';

export default function PropertyChip() {
    const { t } = useTranslation();
    const {
        filters, setPropertyIds, accessiblePropertyIds, hasExplicitPropertyScope,
    } = useDashboardFilters();
    const { properties, loading, error } = useProperties();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const anchorRef = useRef(null);

    const selectedIds = filters.propertyIds || [];
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    // Universe of properties the chip can choose from.
    //   - With an explicit scope (e.g. tenant user): intersect with that scope.
    //   - Without (admin / sadmin): use everything the API returned for the
    //     concessionaire — propertyAccess is empty, so don't filter.
    const universe = useMemo(() => {
        if (!hasExplicitPropertyScope) return properties;
        const accessSet = new Set(accessiblePropertyIds);
        return properties.filter(p => accessSet.has(p.id));
    }, [properties, accessiblePropertyIds, hasExplicitPropertyScope]);

    const universeCount = universe.length;

    const visible = useMemo(() => {
        if (!search.trim()) return universe;
        const q = search.trim().toLowerCase();
        return universe.filter(p =>
            p.name.toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q)
        );
    }, [universe, search]);

    const label = useMemo(() => {
        if (!selectedIds.length) {
            return universeCount ? t('All properties ({{count}})', { count: universeCount }) : t('All properties');
        }
        if (selectedIds.length === 1) {
            const hit = universe.find(p => p.id === selectedIds[0]);
            return hit?.name || t('1 property');
        }
        return t('{{count}} properties', { count: selectedIds.length });
    }, [selectedIds, universe, universeCount, t]);

    // Hide the chip when the user truly has only one property to pick from.
    // For unrestricted users we wait until the property list has loaded so we
    // don't flicker the chip in/out during the initial fetch.
    const onlyOne = hasExplicitPropertyScope
        ? accessiblePropertyIds.length === 1
        : (!loading && universeCount === 1);
    if (onlyOne) return null;

    const togglePid = (id) => {
        if (selectedSet.has(id)) {
            setPropertyIds(selectedIds.filter(x => x !== id));
        } else {
            setPropertyIds([...selectedIds, id]);
        }
    };

    const selectAll = () => setPropertyIds([]);
    const clearAll = () => setPropertyIds(accessiblePropertyIds.slice(0, 0)); // empty = "all" for the API

    return (
        <div className="v3-df-chip-wrap">
            <button
                ref={anchorRef}
                type="button"
                className={`v3-df-chip${open ? ' is-open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <span className="v3-df-chip-icon" aria-hidden>🏢</span>
                <span className="v3-df-chip-label">{label}</span>
                <span className="v3-df-chip-caret" aria-hidden>▾</span>
            </button>

            <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchorRef} align="left">
                <div className="v3-df-popover-title">{t('Properties')}</div>

                <input
                    type="text"
                    className="v3-df-search"
                    placeholder={t('Search properties…')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                />

                <div className="v3-df-popover-actions">
                    <button type="button" className="v3-df-link" onClick={selectAll}>{t('Select all')}</button>
                    <button type="button" className="v3-df-link" onClick={clearAll} disabled={!selectedIds.length}>{t('Clear')}</button>
                </div>

                <div className="v3-df-list">
                    {loading && <div className="v3-df-empty">{t('Loading…')}</div>}
                    {error && <div className="v3-df-empty v3-df-error">{error}</div>}
                    {!loading && !error && visible.length === 0 && (
                        <div className="v3-df-empty">{t('No properties match.')}</div>
                    )}
                    {!loading && !error && visible.map(p => {
                        const checked = selectedSet.has(p.id) || selectedIds.length === 0;
                        const isAllMode = selectedIds.length === 0;
                        return (
                            <label key={p.id} className="v3-df-row">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                        // When "all" is selected (empty set), clicking one means "only this one".
                                        if (isAllMode) {
                                            setPropertyIds([p.id]);
                                        } else {
                                            togglePid(p.id);
                                        }
                                    }}
                                />
                                <span className="v3-df-row-name">{p.name}</span>
                                {p.code && <span className="v3-df-row-code">{p.code}</span>}
                            </label>
                        );
                    })}
                </div>
            </Popover>
        </div>
    );
}
